import { useEffect, useMemo, useState } from 'react';

import { useAgentId, useAuth, useRecruiterId } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { AgentPushCard } from '../lib/push/agent-push.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import { agentsRepo, platformBrandsRepo } from '../lib/repositories/earnings-repo.js';
import { recruitersRepo } from '../lib/repositories/recruiters-repo.js';
import {
  callUpdateAgentPayoutDetails,
  callUpdateRecruiterPayoutDetails,
  type UpdatePayoutDetailsInput,
} from '../lib/callables/payout-details.js';
import {
  callUpdateAgentProfile,
  callUpdateRecruiterProfile,
  uploadKycDocument,
  type UpdateProfileInput,
} from '../lib/callables/profile.js';
import { logger } from '../lib/logger.js';
import { money, type AgentProfile, type PlatformBrand, type RecruiterProfile } from '@kilat/shared';
import { Skeleton as UiSkeleton } from '@kilat/ui';

/** Radius tokens, hoisted out of JSX so `no-literal-string` sees no literal. */
const RADIUS_MD = 'var(--radius-md)';
const RADIUS_LG = 'var(--radius-lg)';

/**
 * /profile — single self-service surface for both personas (Sprint 86).
 *
 * Renders sections for whichever ids resolve from claims:
 *   - Agent profile + payout editor + KYC status
 *   - Recruiter profile + payout editor + KYC status
 *
 * Payout edits go through the new callables `updateAgentPayoutDetails`
 * / `updateRecruiterPayoutDetails` because firestore.rules pins direct
 * agent/recruiter doc writes to super-admin. Server validates same
 * Zod shape we send.
 *
 * KYC document upload UI ships later; for now we surface the verified
 * state read-only (set by super-admin in `/superadmin/agents` or
 * `/superadmin/recruiters`) with a clear "we'll prompt for docs when
 * upload lands" copy so the user is never left wondering.
 */
export function ProfilePage() {
  const { user, loading: authLoading } = useAuth();
  const agentId = useAgentId();
  const recruiterId = useRecruiterId();
  const t = useI18n();
  const brand = usePlatformBrand();

  if (authLoading) {
    return (
      <PageShell>
        <Skeleton />
      </PageShell>
    );
  }
  if (!user) {
    return (
      <PageShell>
        <EmptyState
          title={t.t('agent.payouts.signedOutTitle')}
          body={t.t('agent.profile.signedOutBody')}
        />
      </PageShell>
    );
  }
  if (!agentId && !recruiterId) {
    return (
      <PageShell>
        <EmptyState
          title={t.t('agent.profile.notAgentOrRecruiterTitle')}
          body={t.t('agent.profile.notAgentOrRecruiterBody')}
          ctaLabel={t.t('agent.profile.applyCta')}
          ctaHref={`https://${brand.domains.marketing}/become-an-agent`}
        />
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Header dual={Boolean(agentId && recruiterId)} />
      {agentId ? <AgentSection agentId={agentId} /> : null}
      {recruiterId ? <RecruiterSection recruiterId={recruiterId} /> : null}
      {/* This device's push: the next step for each state (on / turn on /
          blocked → settings / iPhone → Home Screen / unsupported). */}
      <AgentPushCard />
    </PageShell>
  );
}

// ── headers ────────────────────────────────────────────────────────────

function Header({ dual }: { readonly dual: boolean }) {
  const t = useI18n();
  return (
    <header>
      <h1 style={{ margin: 0, fontSize: 'var(--text-h1)', fontWeight: 600 }}>
        {t.t('agent.profile.title')}
      </h1>
      <p
        style={{
          margin: 'var(--space-2) 0 0 0',
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          lineHeight: 1.5,
        }}
      >
        {dual ? t.t('agent.profile.headerDual') : t.t('agent.profile.headerSingle')}
      </p>
    </header>
  );
}

// ── agent section ──────────────────────────────────────────────────────

function AgentSection({ agentId }: { readonly agentId: string }) {
  const t = useI18n();
  // Per-listener error + retry. Without onError a denied read left this section
  // loading forever; a missing/unparseable doc (repo emits null) rendered a
  // permanent skeleton because `profile === null` was conflated with "loading".
  // Both are now explicit, each with a Try-again ([[feedback-no-dead-ends]]).
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const profileSub = useMemo(
    () => (cb: (v: AgentProfile | null) => void) =>
      agentsRepo.snapshot(
        agentId,
        (v) => {
          setLoadError(null);
          cb(v);
        },
        (err) => setLoadError(err),
      ),
    [agentId],
  );
  const { data: profile, loading } = useTrackedSnapshot<AgentProfile | null>(
    ['profile-agent', agentId, retryNonce],
    profileSub,
  );
  const retry = () => {
    setLoadError(null);
    setRetryNonce((n) => n + 1);
  };

  if (loadError) {
    return (
      <Card title={t.t('agent.nav.agentSection')}>
        <ProfileNotice
          title={t.t('agent.loadError.agentProfileTitle')}
          body={t.t('agent.loadError.body')}
          retryLabel={t.t('agent.loadError.retry')}
          onRetry={retry}
        />
      </Card>
    );
  }
  if (loading) {
    return <CardSkeleton title={t.t('agent.nav.agentSection')} />;
  }
  if (profile === null) {
    return (
      <Card title={t.t('agent.nav.agentSection')}>
        <ProfileNotice
          title={t.t('agent.profileGone.agentTitle')}
          body={t.t('agent.profileGone.body')}
          retryLabel={t.t('agent.profileGone.retry')}
          onRetry={retry}
        />
      </Card>
    );
  }

  return (
    <Card
      title={t.t('agent.nav.agentSection')}
      badge={profile.code}
      tier={profile.tier}
      status={profile.status}
    >
      <ContactBlock
        name={profile.name}
        email={profile.email}
        phone={profile.phone}
        locale={profile.locale}
        joinedAt={tsMs(profile.createdAt)}
        platformBrandId={profile.platformBrandId}
        onSave={async (input) => {
          await callUpdateAgentProfile(input);
        }}
      />
      <KycBlock
        persona="agent"
        personaId={agentId}
        verifiedAt={profile.kyc?.verifiedAt ? tsMs(profile.kyc.verifiedAt) : null}
        submittedAt={profile.kyc?.submittedAt ? tsMs(profile.kyc.submittedAt) : null}
        icScanRef={profile.kyc?.icScanRef ?? null}
        thresholdSen={profile.payout.thresholdSen}
      />
      <PayoutEditor
        persona="agent"
        initial={profile.payout}
        onSave={async (input) => {
          await callUpdateAgentPayoutDetails(input);
        }}
      />
    </Card>
  );
}

// ── recruiter section ─────────────────────────────────────────────────

function RecruiterSection({ recruiterId }: { readonly recruiterId: string }) {
  const t = useI18n();
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const profileSub = useMemo(
    () => (cb: (v: RecruiterProfile | null) => void) =>
      recruitersRepo.snapshot(
        recruiterId,
        (v) => {
          setLoadError(null);
          cb(v);
        },
        (err) => setLoadError(err),
      ),
    [recruiterId],
  );
  const { data: profile, loading } = useTrackedSnapshot<RecruiterProfile | null>(
    ['profile-recruiter', recruiterId, retryNonce],
    profileSub,
  );
  const retry = () => {
    setLoadError(null);
    setRetryNonce((n) => n + 1);
  };

  if (loadError) {
    return (
      <Card title={t.t('agent.nav.recruiterSection')}>
        <ProfileNotice
          title={t.t('agent.loadError.recruiterProfileCardTitle')}
          body={t.t('agent.loadError.body')}
          retryLabel={t.t('agent.loadError.retry')}
          onRetry={retry}
        />
      </Card>
    );
  }
  if (loading) {
    return <CardSkeleton title={t.t('agent.nav.recruiterSection')} />;
  }
  if (profile === null) {
    return (
      <Card title={t.t('agent.nav.recruiterSection')}>
        <ProfileNotice
          title={t.t('agent.profileGone.recruiterTitle')}
          body={t.t('agent.profileGone.body')}
          retryLabel={t.t('agent.profileGone.retry')}
          onRetry={retry}
        />
      </Card>
    );
  }

  return (
    <Card
      title={t.t('agent.nav.recruiterSection')}
      badge={profile.code}
      tier={profile.tier}
      status={profile.status}
    >
      <ContactBlock
        name={profile.name}
        email={profile.email}
        phone={profile.phone}
        locale={profile.locale}
        joinedAt={tsMs(profile.createdAt)}
        platformBrandId={profile.platformBrandId}
        onSave={async (input) => {
          await callUpdateRecruiterProfile(input);
        }}
      />
      <KycBlock
        persona="recruiter"
        personaId={recruiterId}
        verifiedAt={profile.kyc?.verifiedAt ? tsMs(profile.kyc.verifiedAt) : null}
        submittedAt={profile.kyc?.submittedAt ? tsMs(profile.kyc.submittedAt) : null}
        icScanRef={profile.kyc?.icScanRef ?? null}
        thresholdSen={profile.payout.thresholdSen}
      />
      <PayoutEditor
        persona="recruiter"
        initial={profile.payout}
        onSave={async (input) => {
          await callUpdateRecruiterPayoutDetails(input);
        }}
      />
    </Card>
  );
}

// ── shared blocks ──────────────────────────────────────────────────────

function ContactBlock({
  name,
  email,
  phone,
  locale,
  joinedAt,
  platformBrandId,
  onSave,
}: {
  readonly name: string;
  readonly email: string;
  readonly phone: string;
  readonly locale: UpdateProfileInput['locale'];
  readonly joinedAt: number;
  readonly platformBrandId: string;
  readonly onSave: (input: UpdateProfileInput) => Promise<void>;
}) {
  const t = useI18n();
  const [brand, setBrand] = useState<PlatformBrand | null>(null);
  useEffect(() => {
    const unsub = platformBrandsRepo.snapshot(platformBrandId, (v) => setBrand(v));
    return unsub;
  }, [platformBrandId]);

  const [nameInput, setNameInput] = useState(name);
  const [phoneInput, setPhoneInput] = useState(phone);
  const [localeInput, setLocaleInput] = useState<UpdateProfileInput['locale']>(locale);
  const [stage, setStage] = useState<
    { kind: 'idle' } | { kind: 'saving' } | { kind: 'saved' } | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  useEffect(() => {
    setNameInput(name);
    setPhoneInput(phone);
    setLocaleInput(locale);
    setStage({ kind: 'idle' });
  }, [name, phone, locale]);

  const dirty =
    nameInput.trim() !== name.trim() ||
    phoneInput.trim() !== phone.trim() ||
    localeInput !== locale;

  async function handleSave() {
    if (nameInput.trim().length < 1 || phoneInput.trim().length < 7) {
      setStage({ kind: 'error', message: 'Name + phone are required.' });
      return;
    }
    setStage({ kind: 'saving' });
    try {
      await onSave({
        name: nameInput.trim(),
        phone: phoneInput.trim(),
        locale: localeInput,
      });
      setStage({ kind: 'saved' });
    } catch (e) {
      logger.error('profile update failed', { error: String(e) });
      setStage({ kind: 'error', message: String((e as Error).message ?? 'Could not save.') });
    }
  }

  function handleReset() {
    setNameInput(name);
    setPhoneInput(phone);
    setLocaleInput(locale);
    setStage({ kind: 'idle' });
  }

  return (
    <section style={blockStyle}>
      <SectionLabel>{t.t('agent.profile.contact')}</SectionLabel>
      <div style={fieldGridStyle}>
        <Field
          label={t.t('agent.profile.fieldName')}
          value={nameInput}
          onChange={setNameInput}
          autoComplete="name"
        />
        <Field
          label={t.t('agent.profile.fieldPhone')}
          value={phoneInput}
          onChange={setPhoneInput}
          autoComplete="tel"
          inputMode="tel"
        />
        <label style={{ display: 'grid', gap: 'var(--space-2)' }}>
          <span style={fieldLabelStyle}>{t.t('agent.profile.locale')}</span>
          <select
            value={localeInput}
            onChange={(e) => setLocaleInput(e.target.value as UpdateProfileInput['locale'])}
            style={inputStyle}
          >
            {/* Language names stay in their own script — intentionally not translated;
                the value tokens are locale enums, not user copy. */}
            {/* eslint-disable i18next/no-literal-string */}
            <option value="en">English</option>
            <option value="ms">Bahasa Malaysia</option>
            <option value="zh">简体中文</option>
            <option value="ta">தமிழ்</option>
            {/* eslint-enable i18next/no-literal-string */}
          </select>
        </label>
      </div>
      <dl style={{ ...dlStyle, marginTop: 'var(--space-2)' }}>
        <DescItem label={t.t('agent.profile.fieldEmail')} value={email} />
        <DescItem
          label={t.t('agent.profile.fieldJoined')}
          value={joinedAt ? formatDate(joinedAt) : '—'}
        />
        <DescItem label={t.t('agent.profile.fieldBrand')} value={brand?.name ?? platformBrandId} />
      </dl>
      <p style={hintStyle}>{t.t('agent.profile.emailHint')}</p>

      {stage.kind === 'error' ? (
        <p role="alert" style={errorStyle}>
          {stage.message}
        </p>
      ) : null}
      {stage.kind === 'saved' && !dirty ? (
        <p
          role="status"
          style={{ margin: 0, fontSize: 'var(--text-meta)', color: 'var(--color-primary)' }}
        >
          {t.t('agent.profile.contactSaved')}
        </p>
      ) : null}

      <div style={actionRowStyle}>
        {dirty ? (
          <button type="button" onClick={handleReset} style={ghostButtonStyle}>
            {t.t('agent.profile.reset')}
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={!dirty || stage.kind === 'saving'}
          style={{
            ...primaryButtonStyle,
            opacity: !dirty || stage.kind === 'saving' ? 0.55 : 1,
            cursor: !dirty || stage.kind === 'saving' ? 'not-allowed' : 'pointer',
          }}
        >
          {stage.kind === 'saving'
            ? t.t('agent.profile.saving')
            : dirty
              ? t.t('agent.profile.saveContact')
              : t.t('agent.profile.noChanges')}
        </button>
      </div>
    </section>
  );
}

function KycBlock({
  persona,
  personaId,
  verifiedAt,
  submittedAt,
  icScanRef,
  thresholdSen,
}: {
  readonly persona: 'agent' | 'recruiter';
  readonly personaId: string;
  readonly verifiedAt: number | null;
  readonly submittedAt: number | null;
  readonly icScanRef: string | null;
  readonly thresholdSen: number;
}) {
  const t = useI18n();
  const [uploadStage, setUploadStage] = useState<
    | { kind: 'idle' }
    | { kind: 'uploading'; fraction: number; filename: string }
    | { kind: 'registering' }
    | { kind: 'done' }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  async function handleFile(file: File) {
    setUploadStage({ kind: 'uploading', fraction: 0, filename: file.name });
    try {
      await uploadKycDocument({
        persona,
        personaId,
        file,
        onProgress: (fraction) => {
          setUploadStage((prev) => (prev.kind === 'uploading' ? { ...prev, fraction } : prev));
        },
      });
      setUploadStage({ kind: 'registering' });
      setUploadStage({ kind: 'done' });
    } catch (e) {
      logger.error('kyc upload failed', { persona, error: String(e) });
      setUploadStage({
        kind: 'error',
        message: String((e as Error).message ?? 'Upload failed.'),
      });
    }
  }

  return (
    <section style={blockStyle}>
      <SectionLabel>KYC</SectionLabel>
      {verifiedAt ? (
        <p style={{ margin: 0, fontSize: 'var(--text-meta)' }}>
          <strong style={{ color: 'var(--color-primary)' }}>
            {t.t('agent.profile.kycVerified')}
          </strong>{' '}
          {t.t('agent.profile.kycVerifiedRest', {
            date: formatDate(verifiedAt),
            amount: money.format(thresholdSen),
          })}
        </p>
      ) : submittedAt && icScanRef ? (
        <div
          style={{
            padding: 'var(--space-3)',
            background: 'rgba(13,122,79,0.04)',
            border: '1px solid rgba(13,122,79,0.18)',
            borderRadius: 'var(--radius-sm)',
          }}
        >
          <strong style={{ color: 'var(--color-primary)', fontSize: 'var(--text-meta)' }}>
            {t.t('agent.profile.kycSubmitted', { date: formatDate(submittedAt) })}
          </strong>
          <p
            style={{
              margin: 'var(--space-1) 0 0 0',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              lineHeight: 1.5,
            }}
          >
            {t.t('agent.profile.kycSubmittedBody')}
          </p>
        </div>
      ) : (
        <div style={kycPendingStyle}>
          <strong style={{ color: '#92400e', fontSize: 'var(--text-meta)' }}>
            {t.t('agent.profile.kycPending')}
          </strong>
          <p
            style={{
              margin: 'var(--space-1) 0 0 0',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            {t.t('agent.profile.kycPendingBody', { amount: money.format(thresholdSen) })}
          </p>
        </div>
      )}

      <UploadWidget
        stage={uploadStage}
        onFile={(f) => void handleFile(f)}
        hasPrevious={Boolean(icScanRef)}
      />
    </section>
  );
}

function UploadWidget({
  stage,
  onFile,
  hasPrevious,
}: {
  readonly stage:
    | { kind: 'idle' }
    | { kind: 'uploading'; fraction: number; filename: string }
    | { kind: 'registering' }
    | { kind: 'done' }
    | { kind: 'error'; message: string };
  readonly onFile: (f: File) => void;
  readonly hasPrevious: boolean;
}) {
  const t = useI18n();
  const inputId = `kyc-upload-${Math.random().toString(36).slice(2, 9)}`;
  const buttonLabel = (() => {
    if (stage.kind === 'uploading') return `Uploading ${Math.round(stage.fraction * 100)}%…`;
    if (stage.kind === 'registering') return 'Registering…';
    if (stage.kind === 'done') return 'Uploaded · choose another to replace';
    if (hasPrevious) return 'Replace document';
    return 'Choose a document to upload';
  })();

  return (
    <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
      <label
        htmlFor={inputId}
        style={{
          ...primaryButtonStyle,
          opacity: stage.kind === 'uploading' || stage.kind === 'registering' ? 0.55 : 1,
          cursor: stage.kind === 'uploading' || stage.kind === 'registering' ? 'wait' : 'pointer',
          textAlign: 'center',
          display: 'inline-block',
        }}
      >
        {buttonLabel}
      </label>
      <input
        id={inputId}
        type="file"
        accept="image/jpeg,image/png,image/webp,application/pdf"
        disabled={stage.kind === 'uploading' || stage.kind === 'registering'}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onFile(file);
          // Reset so re-selecting the same file fires onChange again
          e.target.value = '';
        }}
        style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
      />

      {stage.kind === 'uploading' ? (
        <div
          role="progressbar"
          aria-valuenow={Math.round(stage.fraction * 100)}
          aria-valuemin={0}
          aria-valuemax={100}
          style={{
            height: 6,
            borderRadius: 4,
            background: 'var(--color-border)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${Math.round(stage.fraction * 100)}%`,
              height: '100%',
              background: 'var(--color-primary)',
              transition: 'width 120ms ease',
            }}
          />
        </div>
      ) : null}
      {stage.kind === 'error' ? (
        <p role="alert" style={errorStyle}>
          {stage.message}
        </p>
      ) : null}
      {stage.kind === 'done' ? (
        <p
          role="status"
          style={{ margin: 0, fontSize: 'var(--text-meta)', color: 'var(--color-primary)' }}
        >
          {t.t('agent.profile.uploadDone')}
        </p>
      ) : null}
    </div>
  );
}

function PayoutEditor({
  persona,
  initial,
  onSave,
}: {
  readonly persona: 'agent' | 'recruiter';
  readonly initial: {
    readonly bankName?: string | undefined;
    readonly accountName?: string | undefined;
    readonly accountNumber?: string | undefined;
    readonly duitNowId?: string | undefined;
    readonly thresholdSen: number;
  };
  readonly onSave: (input: UpdatePayoutDetailsInput) => Promise<void>;
}) {
  const t = useI18n();
  const [bankName, setBankName] = useState(initial.bankName ?? '');
  const [accountName, setAccountName] = useState(initial.accountName ?? '');
  const [accountNumber, setAccountNumber] = useState(initial.accountNumber ?? '');
  const [duitNowId, setDuitNowId] = useState(initial.duitNowId ?? '');
  const [thresholdRm, setThresholdRm] = useState(
    ((initial.thresholdSen ?? 10000) / 100).toString(),
  );

  const [stage, setStage] = useState<
    { kind: 'idle' } | { kind: 'saving' } | { kind: 'saved' } | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  // Re-hydrate when the snapshot refreshes after a save (or a super-admin edit).
  useEffect(() => {
    setBankName(initial.bankName ?? '');
    setAccountName(initial.accountName ?? '');
    setAccountNumber(initial.accountNumber ?? '');
    setDuitNowId(initial.duitNowId ?? '');
    setThresholdRm(((initial.thresholdSen ?? 10000) / 100).toString());
    if (stage.kind === 'saved') {
      // leave the "Saved." note up briefly; cleared by next edit
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initial.bankName,
    initial.accountName,
    initial.accountNumber,
    initial.duitNowId,
    initial.thresholdSen,
  ]);

  const dirty =
    bankName !== (initial.bankName ?? '') ||
    accountName !== (initial.accountName ?? '') ||
    accountNumber !== (initial.accountNumber ?? '') ||
    duitNowId !== (initial.duitNowId ?? '') ||
    String((initial.thresholdSen ?? 10000) / 100) !== thresholdRm;

  const hasDestination = bankName.trim() !== '' || duitNowId.trim() !== '';

  async function handleSave() {
    setStage({ kind: 'saving' });
    const thresholdNum = Number(thresholdRm);
    if (!Number.isFinite(thresholdNum) || thresholdNum < 0) {
      setStage({ kind: 'error', message: 'Threshold must be a non-negative number (RM).' });
      return;
    }
    if (bankName.trim() && (!accountName.trim() || !accountNumber.trim())) {
      setStage({
        kind: 'error',
        message: 'Bank account needs both account name and account number.',
      });
      return;
    }
    try {
      await onSave({
        ...(bankName.trim() ? { bankName: bankName.trim() } : {}),
        ...(accountName.trim() ? { accountName: accountName.trim() } : {}),
        ...(accountNumber.trim() ? { accountNumber: accountNumber.trim() } : {}),
        ...(duitNowId.trim() ? { duitNowId: duitNowId.trim() } : {}),
        thresholdSen: Math.max(0, Math.round(thresholdNum * 100)),
      });
      setStage({ kind: 'saved' });
    } catch (e) {
      logger.error(`${persona} payout update failed`, { error: String(e) });
      setStage({ kind: 'error', message: String((e as Error).message ?? 'Could not save.') });
    }
  }

  function handleReset() {
    setBankName(initial.bankName ?? '');
    setAccountName(initial.accountName ?? '');
    setAccountNumber(initial.accountNumber ?? '');
    setDuitNowId(initial.duitNowId ?? '');
    setThresholdRm(((initial.thresholdSen ?? 10000) / 100).toString());
    setStage({ kind: 'idle' });
  }

  return (
    <section style={blockStyle}>
      <SectionLabel>{t.t('agent.profile.payoutDetails')}</SectionLabel>
      <p style={hintStyle}>{t.t('agent.profile.payoutHint')}</p>

      <div style={fieldGridStyle}>
        <Field
          label={t.t('agent.profile.bankName')}
          value={bankName}
          onChange={setBankName}
          placeholder={t.t('agent.profile.bankNamePlaceholder')}
          autoComplete="organization"
        />
        <Field
          label={t.t('agent.profile.accountName')}
          value={accountName}
          onChange={setAccountName}
          placeholder={t.t('agent.profile.accountNamePlaceholder')}
          autoComplete="name"
        />
        <Field
          label={t.t('agent.profile.accountNumber')}
          value={accountNumber}
          onChange={setAccountNumber}
          placeholder={t.t('agent.profile.accountNumberPlaceholder')}
          inputMode="numeric"
          autoComplete="off"
        />
        <Field
          label={t.t('agent.profile.duitNowId')}
          value={duitNowId}
          onChange={setDuitNowId}
          placeholder={t.t('agent.profile.duitNowIdPlaceholder')}
          autoComplete="off"
        />
        <Field
          label={t.t('agent.profile.threshold')}
          value={thresholdRm}
          onChange={setThresholdRm}
          inputMode="decimal"
          tabularNums
          autoComplete="off"
        />
      </div>

      {!hasDestination ? (
        <p
          role="status"
          style={{
            margin: 0,
            padding: 'var(--space-2) var(--space-3)',
            background: 'rgba(245,158,11,0.06)',
            border: '1px solid rgba(245,158,11,0.22)',
            borderRadius: 'var(--radius-sm)',
            fontSize: 'var(--text-meta)',
            color: '#92400e',
          }}
        >
          {t.t('agent.profile.noPayoutDest')}
        </p>
      ) : null}

      {stage.kind === 'error' ? (
        <p role="alert" style={errorStyle}>
          {stage.message}
        </p>
      ) : null}
      {stage.kind === 'saved' && !dirty ? (
        <p
          role="status"
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-primary)',
          }}
        >
          {t.t('agent.profile.payoutSaved')}
        </p>
      ) : null}

      <div style={actionRowStyle}>
        {dirty ? (
          <button type="button" onClick={handleReset} style={ghostButtonStyle}>
            {t.t('agent.profile.reset')}
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={!dirty || stage.kind === 'saving'}
          style={{
            ...primaryButtonStyle,
            opacity: !dirty || stage.kind === 'saving' ? 0.55 : 1,
            cursor: !dirty || stage.kind === 'saving' ? 'not-allowed' : 'pointer',
          }}
        >
          {stage.kind === 'saving'
            ? t.t('agent.profile.saving')
            : dirty
              ? t.t('agent.profile.savePayoutDetails')
              : t.t('agent.profile.noChanges')}
        </button>
      </div>
    </section>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  inputMode,
  tabularNums,
  autoComplete,
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (s: string) => void;
  readonly placeholder?: string;
  readonly inputMode?: 'numeric' | 'decimal' | 'tel' | 'text';
  readonly tabularNums?: boolean;
  readonly autoComplete?: string;
}) {
  return (
    <label style={{ display: 'grid', gap: 'var(--space-2)' }}>
      <span style={fieldLabelStyle}>{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        inputMode={inputMode}
        autoComplete={autoComplete}
        style={{ ...inputStyle, ...(tabularNums ? { fontVariantNumeric: 'tabular-nums' } : {}) }}
      />
    </label>
  );
}

// ── shell ──────────────────────────────────────────────────────────────

function PageShell({ children }: { readonly children: React.ReactNode }) {
  return (
    <main
      className="ag-page"
      style={{
        minHeight: '100dvh',
        background: 'var(--tint-subtle)',
        padding: 'var(--space-4) var(--space-4) var(--space-12) var(--space-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-4)',
        margin: '0 auto',
      }}
    >
      {children}
    </main>
  );
}

function Card({
  title,
  badge,
  tier,
  status,
  children,
}: {
  readonly title: string;
  readonly badge?: string;
  readonly tier?: string;
  readonly status?: string;
  readonly children: React.ReactNode;
}) {
  return (
    <section
      style={{
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-5)',
        boxShadow: 'var(--shadow-1)',
        display: 'grid',
        gap: 'var(--space-4)',
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--space-3)',
          flexWrap: 'wrap',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600 }}>{title}</h2>
        <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          {tier ? <Chip label={`Tier ${tier}`} /> : null}
          {status ? <Chip label={status} accent /> : null}
          {badge ? (
            <code
              style={{
                fontFamily: 'ui-monospace, monospace',
                background: 'var(--color-border)',
                padding: '4px 10px',
                borderRadius: 6,
                fontSize: 'var(--text-meta)',
                fontWeight: 600,
              }}
            >
              {badge}
            </code>
          ) : null}
        </div>
      </header>
      {children}
    </section>
  );
}

function CardSkeleton({ title }: { readonly title: string }) {
  return (
    <section
      style={{
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-5)',
        boxShadow: 'var(--shadow-1)',
      }}
      aria-busy="true"
    >
      <h2 style={{ margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600 }}>{title}</h2>
      <div
        style={{
          height: 220,
          marginTop: 'var(--space-4)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--color-border)',
        }}
      />
    </section>
  );
}

/**
 * In-card error / gone notice with a Try-again. Replaces the old unreachable
 * "Error" badge branch: a denied read or a missing/unparseable profile doc now
 * lands here with an explicit next action instead of a permanent skeleton
 * (per [[feedback-no-dead-ends]]).
 */
function ProfileNotice({
  title,
  body,
  retryLabel,
  onRetry,
}: {
  readonly title: string;
  readonly body: string;
  readonly retryLabel: string;
  readonly onRetry: () => void;
}) {
  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)', justifyItems: 'start' }}>
      <p style={{ margin: 0, fontWeight: 600, fontSize: 'var(--text-body)' }}>{title}</p>
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          lineHeight: 1.5,
        }}
      >
        {body}
      </p>
      <button
        type="button"
        onClick={onRetry}
        style={{ ...primaryButtonStyle, minHeight: 'var(--touch-target, 44px)' }}
      >
        {retryLabel}
      </button>
    </div>
  );
}

function Skeleton() {
  // Shared shimmer from @kilat/ui. Two different heights here, so two calls
  // rather than a `count` — the profile loads a summary strip above a tall
  // form, and a uniform block would misrepresent what is arriving.
  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)' }} aria-busy="true">
      <UiSkeleton height={64} radius={RADIUS_MD} />
      <UiSkeleton height={280} radius={RADIUS_LG} />
    </div>
  );
}

function EmptyState({
  title,
  body,
  ctaLabel,
  ctaHref,
}: {
  readonly title: string;
  readonly body: string;
  readonly ctaLabel?: string;
  readonly ctaHref?: string;
}) {
  return (
    <section
      style={{
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-6)',
        textAlign: 'center',
        boxShadow: 'var(--shadow-1)',
      }}
    >
      <h2 style={{ margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600 }}>{title}</h2>
      <p
        style={{
          margin: 'var(--space-3) auto 0 auto',
          maxWidth: 420,
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          lineHeight: 1.5,
        }}
      >
        {body}
      </p>
      {ctaLabel && ctaHref ? (
        <a href={ctaHref} style={{ ...primaryButtonStyle, marginTop: 'var(--space-4)' }}>
          {ctaLabel}
        </a>
      ) : null}
    </section>
  );
}

function SectionLabel({ children }: { readonly children: React.ReactNode }) {
  return (
    <h3
      style={{
        margin: 0,
        fontSize: 'var(--text-meta)',
        fontWeight: 600,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: 'var(--color-on-surface-muted)',
      }}
    >
      {children}
    </h3>
  );
}

function Chip({ label, accent }: { readonly label: string; readonly accent?: boolean }) {
  return (
    <span
      style={{
        padding: '4px 10px',
        borderRadius: 'var(--radius-pill)',
        background: accent ? 'rgba(13,122,79,0.10)' : 'var(--color-border)',
        color: accent ? 'var(--color-primary)' : 'var(--color-on-surface)',
        fontSize: 'var(--text-meta)',
        fontWeight: 500,
        textTransform: 'capitalize',
      }}
    >
      {label}
    </span>
  );
}

function DescItem({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div>
      <dt style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
        {label}
      </dt>
      <dd
        style={{
          margin: 'var(--space-1) 0 0 0',
          fontSize: 'var(--text-body)',
          fontWeight: 500,
        }}
      >
        {value}
      </dd>
    </div>
  );
}

// ── helpers ────────────────────────────────────────────────────────────

function tsMs(ts: { toMillis?: () => number; seconds?: number } | unknown): number {
  if (!ts || typeof ts !== 'object') return 0;
  const obj = ts as { toMillis?: () => number; seconds?: number; nanoseconds?: number };
  if (typeof obj.toMillis === 'function') return obj.toMillis();
  if (typeof obj.seconds === 'number') {
    return obj.seconds * 1000 + Math.floor((obj.nanoseconds ?? 0) / 1_000_000);
  }
  return 0;
}

function formatDate(ms: number): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ── styles ─────────────────────────────────────────────────────────────

const blockStyle: React.CSSProperties = {
  display: 'grid',
  gap: 'var(--space-3)',
  padding: 'var(--space-4)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-border)',
};

const dlStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 160px), 1fr))',
  gap: 'var(--space-3) var(--space-4)',
  margin: 0,
};

const hintStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  lineHeight: 1.5,
};

const fieldLabelStyle: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  fontWeight: 500,
};

const fieldGridStyle: React.CSSProperties = {
  display: 'grid',
  gap: 'var(--space-3)',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))',
};

const inputStyle: React.CSSProperties = {
  padding: '12px 14px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--color-border-strong)',
  fontSize: 'var(--text-body)',
  fontFamily: 'inherit',
  background: 'var(--color-surface)',
  color: 'var(--color-on-surface)',
  width: '100%',
};

const actionRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 'var(--space-2)',
  justifyContent: 'flex-end',
};

const primaryButtonStyle: React.CSSProperties = {
  background: 'var(--color-primary)',
  color: 'var(--color-primary-fg)',
  padding: '10px 18px',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  fontWeight: 500,
  fontSize: 'var(--text-body)',
  cursor: 'pointer',
  boxShadow: 'var(--shadow-2)',
};

const ghostButtonStyle: React.CSSProperties = {
  background: 'transparent',
  color: 'var(--color-on-surface-muted)',
  padding: '10px 18px',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-body)',
  fontWeight: 500,
  cursor: 'pointer',
};

const errorStyle: React.CSSProperties = {
  margin: 0,
  padding: 'var(--space-2) var(--space-3)',
  background: 'rgba(239,68,68,0.06)',
  border: '1px solid rgba(239,68,68,0.22)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--text-meta)',
  color: '#b91c1c',
};

const kycPendingStyle: React.CSSProperties = {
  padding: 'var(--space-3)',
  background: 'rgba(245,158,11,0.06)',
  border: '1px solid rgba(245,158,11,0.22)',
  borderRadius: 'var(--radius-sm)',
};
